import React, { useEffect, useMemo } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { FullScreenLoader } from '~/components/FullScreenLoader';
import { PageWithHeader } from '~/components/PageWithHeader';
import { PageWithSidebar } from '~/components/PageWithSidebar';
import { ADMIN_ROUTES, ROUTES } from '~/constants/routes';
import { CompaniesPage } from '~/pages/Companies';
import { CompanyPage } from '~/pages/Company';
import { CreateCompanyPage } from '~/pages/CreateCompany';
import { CreateUserPage } from '~/pages/CreateUser';
import { DeletedCompaniesPage } from '~/pages/DeletedCompanies';
import { ExternalUsersPage } from '~/pages/ExternalUsers';
import { GeneratedLinkPage } from '~/pages/GeneratedLink';
import { LoginPage } from '~/pages/Login';
import { NotFoundPage } from '~/pages/NotFound';
import { PitchbookPage } from '~/pages/Pitchbook';
import { UsersPage } from '~/pages/Users';
import { useValidateLinkMutation } from '~/services/rtkQuery/admin';
import { useGetSecurityTypesQuery } from '~/services/rtkQuery/company';
import { useGetMeQuery } from '~/services/rtkQuery/user';
import { useAppSelector } from '~/store/hooks';
import { selectGeneratedLink } from '~/store/slices/generatedLink/selectors';
import { selectIsUserAuthenticated, selectUserRole } from '~/store/slices/user/selectors';

export const Router: React.FC = () => {
  const generatedLink = useAppSelector(selectGeneratedLink);
  const isUserAuthenticated = useAppSelector(selectIsUserAuthenticated);
  const userRole = useAppSelector(selectUserRole);
  const getUserQuery = useGetMeQuery(undefined, { skip: !isUserAuthenticated });
  const getSecurityTypesQuery = useGetSecurityTypesQuery(undefined, { skip: !isUserAuthenticated });
  const [validateLinkMutation, { isLoading: isLoadingValidateLinkMutation }] =
    useValidateLinkMutation();

  const link = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    return searchParams.get('link');
  }, []);

  const isGeneratedLinkRoute = useMemo(() => {
    return (
      window.location.pathname.startsWith('/companies') &&
      window.location.pathname.endsWith('/edit') &&
      !!link
    );
  }, [link]);

  useEffect(() => {
    if (isGeneratedLinkRoute) {
      (async () => {
        try {
          const response = await validateLinkMutation({ id: link as string }).unwrap();

          if (response.result.status !== 'activated') {
            window.location.replace(`${ROUTES.GENERATED_LINK}?status=${response.result.status}`);
          }
        } catch (e) {
          window.location.replace(ROUTES.GENERATED_LINK);
        }
      })();
    }
  }, [isGeneratedLinkRoute, link, validateLinkMutation]);

  if (
    getUserQuery.isLoading ||
    getSecurityTypesQuery.isLoading ||
    isLoadingValidateLinkMutation ||
    (isUserAuthenticated && !userRole) ||
    (isGeneratedLinkRoute && !generatedLink)
  ) {
    return <FullScreenLoader loading transparent={false} />;
  }

  return (
    <RouterProvider
      router={createBrowserRouter([
        {
          element: isUserAuthenticated ? <Navigate to={ROUTES.HOME} /> : <PageWithHeader />,
          children: [
            {
              path: ROUTES.LOGIN,
              Component: LoginPage,
            },
          ],
        },
        {
          element:
            isUserAuthenticated || isGeneratedLinkRoute ? (
              <PageWithHeader />
            ) : (
              <Navigate to={ROUTES.LOGIN} />
            ),
          children: [
            {
              path: ROUTES.HOME,
              element: <Navigate to={ROUTES.COMPANIES} />,
            },
            {
              path: ROUTES.COMPANIES,
              Component: CompaniesPage,
            },
            {
              path: ROUTES.CREATE_COMPANY,
              Component: CreateCompanyPage,
            },
            {
              path: ROUTES.PITCHBOOK,
              Component: PitchbookPage,
            },
            {
              path: ROUTES.COMPANY,
              Component: CompanyPage,
            },
            {
              path: ROUTES.EDIT_COMPANY,
              Component: CreateCompanyPage,
            },
          ],
        },
        {
          element: isUserAuthenticated ? <Navigate to={ADMIN_ROUTES.HOME} /> : <PageWithHeader />,
          children: [
            {
              path: ADMIN_ROUTES.LOGIN,
              Component: LoginPage,
            },
          ],
        },
        {
          element: isUserAuthenticated ? (
            userRole === 'admin' || userRole === 'super_admin' ? (
              <PageWithSidebar />
            ) : (
              <Navigate to={ROUTES.HOME} />
            )
          ) : (
            <Navigate to={ADMIN_ROUTES.LOGIN} />
          ),
          children: [
            {
              path: ADMIN_ROUTES.HOME,
              element: <Navigate to={ADMIN_ROUTES.DELETED_COMPANIES} />,
            },
            {
              path: ADMIN_ROUTES.USERS,
              Component: UsersPage,
            },
            {
              path: ADMIN_ROUTES.DELETED_COMPANIES,
              Component: DeletedCompaniesPage,
            },
            {
              path: ADMIN_ROUTES.CREATE_USER,
              Component: CreateUserPage,
            },
            {
              path: ADMIN_ROUTES.EDIT_USER,
              Component: CreateUserPage,
            },
            {
              path: ADMIN_ROUTES.EXTERNAL_USERS,
              Component: ExternalUsersPage,
            },
          ],
        },
        {
          Component: PageWithHeader,
          children: [
            {
              path: ROUTES.GENERATED_LINK,
              Component: GeneratedLinkPage,
            },
            {
              path: ROUTES.NOT_FOUND,
              Component: NotFoundPage,
            },
          ],
        },
      ])}
    />
  );
};
